// Phase 24 — section metadata for the FE permission hook.
//
// Source of truth: server/src/lib/sectionRegistry.js. Keep in sync.
// CASE_INTERNAL_KEYS now lists the view/create/edit/delete quad keys plus the
// retained operation keys. ALIASES maps legacy call-site keys (still used by
// not-yet-rewired FE gates) onto their canonical new key, mirroring the server
// normalizer so client-side resolution matches the server exactly.

const QUAD_CASE_INTERNAL_BASES = [
  "caseView.identity",
  "caseView.parties",
  "caseView.terms",
  "caseView.financials",
  "caseView.timeline",
  // 182 A.2 — the Notes section (the contact timeline). Mirrored here (the
  // Phase 37 D2 rule) or clientCan skips its per-case grants and the team
  // panels never draw the columns.
  "caseView.notes",
  "caseView.tasks",
  "caseView.timeEntries",
  "caseView.documents",
  "caseView.agenda",
  "caseView.chat",
  "caseView.timelog",
  "caseView.cdd",
  "caseView.calculator",
  "collectiveCase.clientList",
  "collectiveCase.clientDocuments",
  "collectiveCase.retainerRounds",
  "collectiveCase.clientPayments",
  "collectiveCase.contactBuilder",
];

const CASE_INTERNAL_OPS = [
  "caseView.documents.link",
  "caseView.documents.generate",
  "caseView.documents.billing",
  "caseView.emails.archive",
  "caseView.emails.billing",
  // 108-a — the WhatsApp card's own pair. Mirrored here (not only in the server
  // registry) or clientCan skips their per-case grants and TeamAccessPanel
  // never draws the columns, which is the Phase 41 defect three lines down.
  "caseView.whatsapp.archive",
  "caseView.whatsapp.billing",
  "caseView.cdd.sar",
  "caseView.cdd.pep",
  "caseView.cdd.report.export",
  "caseView.successFee.edit",
  // Phase 37 (D2) — reconcile the client case-internal set to the server registry.
  // These three are case-internal + grantable server-side (honored by can() /
  // isBillingDelegate / isProformaDelegate) but were missing here, so clientCan
  // skipped their per-case grants and TeamAccessPanel never surfaced them.
  "caseView.timeEntries.markBilled",
  "caseView.proformas.unsent",
  "caseView.proformas.sent",
  // 179 B.2 — the approve right. Mirrored here (the Phase 37 D2 rule) or
  // clientCan skips its per-case grants and the team panels never draw the
  // column. An ORDINARY can() key — role default or live grant — and
  // deliberately NOT in HELPER_GATED_KEYS below: no automatic RP, ruled.
  "caseView.proformas.approve",
  // 144 §3 — the bulk bar's two rights. Mirrored here (the Phase 37 D2 rule)
  // or clientCan skips their per-case grants and the team panels never draw
  // the columns. Deliberately NOT in HELPER_GATED_KEYS below: these are
  // ordinary can() keys — role default or live grant — and the bulk routes
  // apply the admin-or-RP-of-the-matter bound per row, so an RP does not hold
  // them by construction.
  "caseView.timeEntries.bulkEdit",
  "caseView.timeEntries.bulkDelete",
  // 150 §5 — the block's three new rights (importExport / pillColumn /
  // editAllRows). Mirrored here (the Phase 37 D2 rule) or clientCan skips
  // their per-case grants and the team panels never draw the columns. All
  // three are ordinary can() keys — role default or live grant; editAllRows
  // additionally carries bit:'delete' server-side so it is not RP-delegable
  // until the firm switches it on.
  "caseView.timeEntries.importExport",
  "caseView.timeEntries.pillColumn",
  "caseView.timeEntries.editAllRows",
  // Phase 41 (D-gate) — the Client file visibility right. Must be mirrored here
  // (not just the server registry) or TeamAccessPanel columns + clientCan skip
  // its per-case grants, so a granted collaborator would never see the link.
  "caseView.clientFile.view",
  // Phase 25 — collective bulk-mailer (see vs. fire), CC-scoped.
  "collectiveCase.bulkMailer.view",
  "collectiveCase.bulkMailer.send",
];

export const CASE_INTERNAL_KEYS = new Set([
  ...QUAD_CASE_INTERNAL_BASES.flatMap((b) => [
    `${b}.view`,
    `${b}.create`,
    `${b}.edit`,
    `${b}.delete`,
  ]),
  ...CASE_INTERNAL_OPS,
]);

// Phase 41 (D-gate-3) — helper-gated case-internal rights: their authority is
// admin/RP/grant (no role-default), so clientCan() can't see an RP's authority
// for them. The Case Team panel uses this to SHOW these columns to a granting RP
// (who holds them via RP — the panel is already RP-of-scope-gated). Server
// mirror: HELPER_GATED in server/src/lib/permissions.js — keep the two in sync.
export const HELPER_GATED_KEYS = new Set([
  "caseView.proformas.unsent",
  "caseView.proformas.sent",
  "caseView.timeEntries.markBilled",
  "caseView.clientFile.view",
]);

// Phase 24 (C3) — legacy->native ALIAS SHIM REMOVED. Every FE call site reads
// native quad/native keys directly; clientCan no longer normalizes.

// P20f — THE HIDE LIST, client side.
//
// A different axis from everything above: the sets in this file describe what
// KIND of key a section key is, and the role machinery decides who reaches it.
// This one says whether the firm has the section at all. The client does NOT
// re-derive it — GET /api/permissions/me ships the RESOLVED list (server:
// lib/sectionVisibility.js), because a second derivation is a second source of
// truth, and this one would need the switchboard in the browser to compute.
//
// hiddenFor() is the whole client-side rule, and it is deliberately blunt:
// hidden beats everything, admin included. A hidden section is not a permission
// the user lacks — it is a part of the product this firm does not have, so
// there is nothing to grant, nothing to request, and nothing to draw.
export function hiddenSectionSet(data: any): Set<string> {
  const raw = data?.hiddenSections;
  return new Set(Array.isArray(raw) ? raw.filter((k: any) => typeof k === "string") : []);
}

export function isHiddenSection(data: any, sectionKey: any): boolean {
  if (!sectionKey) return false;
  const raw = data?.hiddenSections;
  return Array.isArray(raw) && raw.indexOf(sectionKey) !== -1;
}

// P21-zero — ONE MAP: which section key governs a LATERAL SURFACE.
//
// A "surface" here is one of the app's own short section ids (`ju`, `lib`,
// `cl`, …) — the value `section` holds, the key the sidebar row carries, the
// id a search chip filters on, the entity a report tile is about. Four
// different places render the same surface, and before this map each decided
// on its own whether to:
//
//   sidebar nav      `if (isAdmin) return true` before consulting can() —
//                    the exact short-circuit hidden() exists to close.
//   the page itself  nothing at all: renderContent() rendered whatever
//                    `section` said, so a hidden page still mounted.
//   search chips     nothing at all.
//   report tiles     the report FAMILY key only (financial / operational),
//                    never the entity the tile is about — so "Jurisdictions"
//                    kept its tile after jurisdictions were hidden.
//
// The rule this restores is the GET/SSE lockstep rule stated in
// server/src/lib/rowScope.js:15 — the two directions of one fact may never
// diverge, and the way to guarantee that is one predicate with no second face
// to drift from. Here the fact is "does this firm's product have this
// surface?", and isHiddenSurface() is that single face.
//
// PERMISSIONS ARE NOT THIS MAP'S BUSINESS. Each surface keeps whatever
// authorization it already had; this adds the visibility term in front of it.
// That is what makes the change provably inert on a tenant that hides nothing
// (WATT): every term it introduces is `false`.
/* 189 §H — the campaigns page's key, a named constant: i18n-total's prose-map
   detector reads a new string value in this object as a literal reaching a
   human, and a section key is not one. */
const CAMPAIGNS_VIEW_KEY = "lateral.campaigns.view";
export const SURFACE_SECTION_KEY: Record<string, string> = {
  // the sidebar's own ids (App.tsx's nav list) …
  ag: "lateral.agenda",
  gchat: "lateral.generalChat",
  campaigns: CAMPAIGNS_VIEW_KEY, // 189 §H
  ca: "lateral.cases.view",
  cc: "lateral.collectiveCases.view",
  ju: "lateral.jurisdictions.view",
  co2int: "lateral.persons.view",
  co2ext: "lateral.persons.view",
  cl: "lateral.persons.view",
  cp: "lateral.persons.view",
  op: "lateral.persons.view",
  ocu: "lateral.persons.view",
  oc: "lateral.persons.view",
  ex: "lateral.persons.view",
  me: "lateral.persons.view",
  ar: "lateral.persons.view",
  op2: "lateral.persons.view",
  sched: "lateral.schedule",
  places: "lateral.places.view",
  lib: "lateral.library.view",
  external: "lateral.external",
  invoices: "lateral.invoices.view",
  archive: "lateral.archive",
  costs: "lateral.costsAndFees",
  fleet: "fleet.view", // BRIEF 162 — the operator cockpit (also gated on isCockpit in App.tsx)
  // … plus the ids only the search dialog uses. `co` is the collaborator
  // chip (the sidebar splits the same directory into co2int/co2ext), and the
  // four case-scoped chips search rows that live inside a case.
  co: "lateral.persons.view",
  tk: "caseView.tasks.view",
  te: "caseView.timeEntries.view",
  chat: "caseView.chat.view",
  doc: "caseView.documents.view",
};

/** Does this firm's product lack the surface entirely?
 *
 * A surface with no entry in the map is NEVER hidden — `dash`, `settings` and
 * `reports` are not section-keyed (the first two are universal, the third
 * gates on a pair of family keys inside ReportsView), and answering "hidden"
 * for something the map does not describe would be a guess. */
export function isHiddenSurface(data: any, surfaceKey: any): boolean {
  const sectionKey = SURFACE_SECTION_KEY[surfaceKey as string];
  return sectionKey ? isHiddenSection(data, sectionKey) : false;
}
